import { z } from "zod";
import {
  dataStateSchema,
  freshnessSchema,
  qualitySchema,
  reconciliationSchema,
} from "./governance";
import {
  isoDateTimeSchema,
  opaqueIdSchema,
  permissionKeySchema,
  versionSchema,
} from "./primitives";

export const claimsVolumeMetricIdSchema = z.enum(["K002", "K003", "K004", "K005"]);
export const claimsVolumeSourceChoiceSchema = z.enum(["SOURCE_RANKED", "GOVERNED_DEMONSTRATION"]);
export type ClaimsVolumeSourceChoice = z.infer<typeof claimsVolumeSourceChoiceSchema>;

export const claimsVolumePointSchema = z
  .object({
    periodStart: isoDateTimeSchema,
    value: z.number().int().nonnegative(),
  })
  .strict();

export const claimsVolumeProvenanceSchema = z
  .object({
    sourceConnectionId: opaqueIdSchema,
    ingestionRunId: opaqueIdSchema,
    sourceIdentifier: z.string().trim().min(1).max(500),
    sourceName: z.string().trim().min(1).max(300),
    authorityClass: z.string().trim().min(1).max(100),
  })
  .strict();

export const claimsVolumeSeriesSchema = z
  .object({
    metricId: claimsVolumeMetricIdSchema,
    definitionVersion: versionSchema,
    label: z.string().trim().min(1).max(200),
    status: z.enum(["AVAILABLE", "UNAVAILABLE"]),
    points: z.array(claimsVolumePointSchema).max(120),
    total: z.number().int().nonnegative().optional(),
    calculationRunId: opaqueIdSchema.optional(),
    dataCutoffAt: isoDateTimeSchema.optional(),
    dataState: dataStateSchema,
    authorityClass: z.string().trim().min(1).max(100),
    productionUseProhibited: z.boolean(),
    sourceSummary: z.string().trim().min(1).max(1_000),
    freshness: freshnessSchema,
    quality: qualitySchema,
    reconciliation: reconciliationSchema,
    permissions: z.array(permissionKeySchema).min(1).max(20),
    provenance: z.array(claimsVolumeProvenanceSchema).max(100),
    unavailableReason: z.string().trim().min(1).max(1_000).optional(),
  })
  .strict()
  .superRefine((series, context) => {
    if (series.status === "UNAVAILABLE") {
      if (
        series.points.length > 0 ||
        series.total !== undefined ||
        series.calculationRunId !== undefined ||
        series.dataCutoffAt !== undefined
      ) {
        context.addIssue({
          code: z.ZodIssueCode.custom,
          path: ["points"],
          message: "Unavailable claims-volume series cannot contain values.",
        });
      }
      if (!series.unavailableReason) {
        context.addIssue({
          code: z.ZodIssueCode.custom,
          path: ["unavailableReason"],
          message: "Unavailable claims-volume series require an explicit reason.",
        });
      }
    } else {
      if (!series.calculationRunId || !series.dataCutoffAt) {
        context.addIssue({
          code: z.ZodIssueCode.custom,
          path: ["calculationRunId"],
          message: "Available claims-volume series require a calculation run and data cutoff.",
        });
      }
      if (series.points.length === 0) {
        context.addIssue({
          code: z.ZodIssueCode.custom,
          path: ["points"],
          message: "Available claims-volume series require at least one governed point.",
        });
      }
      if (
        (series.metricId === "K002" || series.metricId === "K003") &&
        series.total === undefined
      ) {
        context.addIssue({
          code: z.ZodIssueCode.custom,
          path: ["total"],
          message: "Available K002 and K003 series require a governed total.",
        });
      }
      if (series.unavailableReason) {
        context.addIssue({
          code: z.ZodIssueCode.custom,
          path: ["unavailableReason"],
          message: "Available claims-volume series cannot carry an unavailable reason.",
        });
      }
    }
    if (
      series.dataState === "SYNTHETIC" &&
      (series.authorityClass !== "DEMONSTRATION_ONLY" || !series.productionUseProhibited)
    ) {
      context.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["authorityClass"],
        message:
          "Synthetic claims-volume series must be demonstration-only and production-prohibited.",
      });
    }
    if (series.dataState !== "SYNTHETIC" && series.productionUseProhibited) {
      context.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["productionUseProhibited"],
        message: "Only explicitly synthetic claims-volume series may be production-prohibited.",
      });
    }
  });

export const claimsVolumeTrendResponseSchema = z
  .object({
    view: z.literal("claims-volume-trend"),
    sourceChoice: claimsVolumeSourceChoiceSchema.optional(),
    schemaVersion: versionSchema,
    tenantId: opaqueIdSchema,
    generatedAt: isoDateTimeSchema,
    dataAsOf: isoDateTimeSchema,
    series: z.array(claimsVolumeSeriesSchema).length(4),
    warnings: z
      .array(
        z
          .object({
            code: z
              .string()
              .trim()
              .regex(/^[A-Z][A-Z0-9_]*$/)
              .max(100),
            message: z.string().trim().min(1).max(1_000),
            metricIds: z.array(claimsVolumeMetricIdSchema).max(4).optional(),
          })
          .strict(),
      )
      .max(20),
  })
  .strict()
  .superRefine((response, context) => {
    const ids = response.series.map(({ metricId }) => metricId);
    const expected = ["K002", "K003", "K004", "K005"];
    if (
      new Set(ids).size !== 4 ||
      expected.some((id) => !ids.includes(id as (typeof ids)[number]))
    ) {
      context.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["series"],
        message: "Claims-volume response must contain K002 through K005 exactly once.",
      });
    }
  });

export type ClaimsVolumeMetricId = z.infer<typeof claimsVolumeMetricIdSchema>;
export type ClaimsVolumePoint = z.infer<typeof claimsVolumePointSchema>;
export type ClaimsVolumeProvenance = z.infer<typeof claimsVolumeProvenanceSchema>;
export type ClaimsVolumeSeries = z.infer<typeof claimsVolumeSeriesSchema>;
export type ClaimsVolumeTrendResponse = z.infer<typeof claimsVolumeTrendResponseSchema>;
